import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  Divider,
  CircularProgress,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { RootState } from "../../../../store/Index";
import { useSelector } from "react-redux";
import { useState } from "react";

interface ConfirmPayoutProps {
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
  onEditDetails?: () => void;
  onConfirm?: () => Promise<void>;
  amount?: number;
  method?: string;
  phoneNumber?: string;
  accountLabel?: string;
}

export default function ConfirmPayout({
  open,
  onClose,
  onBack,
  onEditDetails,
  onConfirm,
  amount = 0,
  method = "",
  phoneNumber = "",
}: ConfirmPayoutProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (!onConfirm) return;
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "30px",
          overflow: "hidden",
          maxWidth: 360,
          width: "100%",
          m: 2,
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <IconButton
              size="small"
              sx={{ color: reduxTheme.primary_colors?.[400], ml: -1 }}
              onClick={onBack || onClose}
            >
              <ArrowBack fontSize="small" />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                color: reduxTheme.primary_colors?.[400],
                fontWeight: 700,
                fontSize: "16px",
              }}
            >
              Confirm Payout
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{
              color: reduxTheme.primary_colors?.[400],
              fontSize: "12px",
              mb: 2,
            }}
          >
            Review the details below before submitting
          </Typography>

          {/* Amount Card */}
          <Box
            sx={{
              background: reduxTheme.primary_colors?.[800],
              borderRadius: "15px",
              p: 2.5,
              textAlign: "center",
              maxWidth: 200,
              width: "100%",
              mb: 3,
              mx: "auto",
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontSize: "10px", display: "block", mb: 0.5 }}
            >
              Payout amount
            </Typography>
            <Typography variant="h4" sx={{ fontSize: "28px", fontWeight: 700 }}>
              ${amount.toFixed(2)}
            </Typography>
          </Box>

          {/* Details */}
          <Box
            sx={{
              mb: 3,
              border: `1px solid ${reduxTheme.primary_colors?.[800]}`,
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 1.5,
                px: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "#666" }}>
                Method
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {method}
              </Typography>
            </Box>
            <Divider sx={{ borderColor: reduxTheme.primary_colors?.[800] }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                py: 1.5,
                px: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "#666" }}>
                Account
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {phoneNumber}
              </Typography>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={onEditDetails}
              disabled={loading}
              sx={{
                flex: 1,
                borderRadius: 3,
                py: 1.5,
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 500,
                border: `1px solid ${reduxTheme.primary_colors?.[800]}`,
                color: reduxTheme.primary_colors?.[100],
                "&:hover": { borderColor: reduxTheme.primary_colors?.[800] },
              }}
            >
              Edit Details
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={loading}
              sx={{
                flex: 1,
                background: `linear-gradient(135deg, ${reduxTheme.primary_colors?.[400]} 0%, ${reduxTheme.primary_colors?.[10]} 100%)`,
                color: reduxTheme.primary_colors?.[900],
                borderRadius: 3,
                py: 1.5,
                textTransform: "none",
                fontSize: "0.9rem",
                fontWeight: 500,
                boxShadow: "none",
              }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Confirm Request"
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
